import '@/styles/globals.css'

import type { CSSProperties, FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import localFont from 'next/font/local'

import {
    Geist,
    Geist_Mono,
    Instrument_Serif
} from 'next/font/google'

/* Cyrene — the display face behind the "Stories In Motion" wordmark.
 * Riccardo Agostinelli, free for personal and commercial use. */
const cyrene = localFont({
    src: '../assets/fonts/cyrene.woff2',
    variable: '--font-cyrene',
    weight: '400',
    display: 'swap'
})

const geistSans = Geist({
    variable: '--font-sans',
    subsets: ['latin']
})

const geistMono = Geist_Mono({
    variable: '--font-geist-mono',
    subsets: ['latin']
})

const instrumentSerif = Instrument_Serif({
    variable: '--font-instrument-serif',
    weight: '400',
    subsets: ['latin']
})

/* Tailwind's font tokens live in `:root` and are written as indirections, e.g.
 * `--font-display: var(--font-cyrene), ...`. But next/font declares
 * `--font-cyrene` on THIS wrapper, not on `:root` — and a var() inside a custom
 * property is substituted where that property is DECLARED. At `:root`
 * `--font-cyrene` is undefined, so `--font-display` resolves to the
 * guaranteed-invalid value and every `font-display` / `font-mono` / `font-serif`
 * utility silently falls back to the inherited sans.
 *
 * Redeclaring the tokens here, with the concrete family names next/font
 * generates, resolves them on an element that actually has the fonts. */
const fontTokens = {
    '--font-sans': geistSans.style.fontFamily,
    '--font-mono': geistMono.style.fontFamily,
    '--font-serif': instrumentSerif.style.fontFamily,
    '--font-display': `${cyrene.style.fontFamily}, ${instrumentSerif.style.fontFamily}, sans-serif`
} as CSSProperties

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    return (
        <div
            style={fontTokens}
            className={`${geistSans.variable} ${geistMono.variable} ${instrumentSerif.variable} ${cyrene.variable} h-full font-sans antialiased`}
        >
            <Component {...pageProps} />
        </div>
    )
}

export default App